import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useNotification } from '../contexts/NotificationContext';
import './Overview.css';
import { getApiUrl } from '../utils/api';
import { formatDateOnly } from '../utils/dateOnly';
import axios from 'axios';

const Overview = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const roleUpper = String(user?.role || '').toUpperCase();
  const isTrialOrUnitario = roleUpper === 'TRIAL' || roleUpper === 'UNITARIO';
  const { showSuccess, showError } = useNotification();
  const tutorial = useTutorial();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [supportSubject, setSupportSubject] = useState('Dúvida no sistema');
  const [supportMessage, setSupportMessage] = useState('');
  const [isSendingSupport, setIsSendingSupport] = useState(false);

  // Dados dos cards organizados por seções
  const cardSections = {
    gerenciamento: {
      title: 'Gerenciamento',
      icon: '⚙️',
      cards: [
        { id: 'projects', title: 'Projetos', icon: '📊', route: '/projects', description: 'Gerencie seus projetos de teste' },
        { id: 'dashboard', title: 'Dashboard', icon: '📈', route: '/dashboard', description: 'Visão geral de métricas e KPIs' },
        { id: 'test-repository', title: 'Repositório de Testes', icon: '📚', route: '/test-repository', description: 'Gerencie o repositório de testes' },
        { id: 'executions', title: 'Execuções', icon: '🚀', route: '/executions', description: 'Acompanhe execuções de teste' },
        { id: 'status', title: 'Status', icon: '🏷️', route: '/status', description: 'Gerencie o repositório de status' },
        { id: 'issues', title: 'Bugs/Melhorias', icon: '🐞', route: '/issues', description: 'Gerencie issues, bugs e melhorias' }
      ]
    },
    integracoes: {
      title: 'Integrações',
      icon: '🔗',
      cards: [
        { id: 'integrations', title: 'Integrações V2', icon: '🔗', route: '/integrations-v2', description: 'Gerencie conexões com GitHub, Jira, GitLab e outros serviços' }
      ]
    },
    seguranca: {
      title: 'Segurança',
      icon: '🔒',
      cards: [
        { id: 'user-management', title: 'Usuários e Funções', icon: '👥', route: '/user-management', description: 'Gerencie usuários, permissões e funções' }
      ]
    }
  };

  // Dados de notícias/updates (carregados da API com fallback dinâmico)
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
  };
  const [newsUpdates, setNewsUpdates] = useState([]);
  
  // Dados de integrações
  const [integrations, setIntegrations] = useState([]);

  useEffect(() => {
    const fetchChangelog = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(getApiUrl('changelog'), {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });

        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data)
            ? data
            : (Array.isArray(data.data) ? data.data : []);
          if (list.length > 0) {
            setNewsUpdates(list);
            return;
          }
        }
        // Fallback para conteúdo dinâmico caso falhe ou retorne vazio
        setNewsUpdates([
          {
            id: 'fallback-1',
            title: 'Nova funcionalidade: Automação de Testes',
            description: 'Agora você pode automatizar seus casos de teste com integração GitHub Actions.',
            date: daysAgo(1),
            type: 'feature',
            icon: '🚀'
          },
          {
            id: 'fallback-2',
            title: 'Melhoria: Interface de Relatórios',
            description: 'Interface de relatórios redesenhada para melhor experiência do usuário.',
            date: daysAgo(3),
            type: 'improvement',
            icon: '📊'
          },
          {
            id: 'fallback-3',
            title: 'Correção: Sincronização com GitHub',
            description: 'Corrigido problema de sincronização com repositórios GitHub.',
            date: daysAgo(7),
            type: 'bugfix',
            icon: '🔧'
          },
          {
            id: 'fallback-4',
            title: 'Nova integração: Slack',
            description: 'Receba notificações dos seus testes diretamente no Slack.',
            date: daysAgo(14),
            type: 'feature',
            icon: '💬'
          }
        ]);
      } catch (error) {
        console.error('Erro ao carregar changelog:', error);
        setNewsUpdates([
          {
            id: 'fallback-1',
            title: 'Nova funcionalidade: Automação de Testes',
            description: 'Agora você pode automatizar seus casos de teste com integração GitHub Actions.',
            date: daysAgo(1),
            type: 'feature',
            icon: '🚀'
          },
          {
            id: 'fallback-2',
            title: 'Melhoria: Interface de Relatórios',
            description: 'Interface de relatórios redesenhada para melhor experiência do usuário.',
            date: daysAgo(3),
            type: 'improvement',
            icon: '📊'
          },
          {
            id: 'fallback-3',
            title: 'Correção: Sincronização com GitHub',
            description: 'Corrigido problema de sincronização com repositórios GitHub.',
            date: daysAgo(7),
            type: 'bugfix',
            icon: '🔧'
          },
          {
            id: 'fallback-4',
            title: 'Nova integração: Slack',
            description: 'Receba notificações dos seus testes diretamente no Slack.',
            date: daysAgo(14),
            type: 'feature',
            icon: '💬'
          }
        ]);
      }
    };

    fetchChangelog();
  }, []);

  // Carregar dados de integrações
  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(getApiUrl('integrations'), {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        
        if (response.data?.success) {
          const statusData = response.data.data || {};
          const integrationItems = [
            { key: 'jira', title: 'Jira', icon: '🟦', configured: !!statusData['jira-basic']?.connected || !!statusData['jira-basic']?.active, active: !!statusData['jira-basic']?.connected || !!statusData['jira-basic']?.active },
            { key: 'github', title: 'GitHub', icon: '🐙', configured: !!statusData.github?.configured, active: !!statusData.github?.active },
            { key: 'gitlab', title: 'GitLab', icon: '🦊', configured: !!statusData.gitlab?.configured, active: !!statusData.gitlab?.active },
            { key: 'bitbucket', title: 'Bitbucket', icon: '📘', configured: !!statusData.bitbucket?.configured, active: !!statusData.bitbucket?.active }
          ];
          setIntegrations(integrationItems);
        }
      } catch (error) {
        console.error('Erro ao carregar integrações:', error);
        // Fallback com dados básicos
        setIntegrations([
          { key: 'jira', title: 'Jira', icon: '🟦', configured: false, active: false },
          { key: 'github', title: 'GitHub', icon: '🐙', configured: false, active: false },
          { key: 'gitlab', title: 'GitLab', icon: '🦊', configured: false, active: false },
          { key: 'bitbucket', title: 'Bitbucket', icon: '📘', configured: false, active: false }
        ]);
      }
    };

    loadIntegrations();
  }, []);

  const handleCardClick = (route) => {
    navigate(route);
  };

  // news.date vem do changelog — data pura (sem hora), precisa do mesmo
  // helper que corrige o bug do "dia anterior" (ver utils/dateOnly.js).
  const formatDate = formatDateOnly;

  const getNewsTypeClass = (type) => {
    switch (type) {
      case 'feature': return 'news-feature';
      case 'improvement': return 'news-improvement';
      case 'bugfix': return 'news-bugfix';
      default: return 'news-default';
    }
  };

  const getPlanInfo = (currentUser) => {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const parseDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const diffDays = (from, to) => {
      const fromDay = startOfDay(from).getTime();
      const toDay = startOfDay(to).getTime();
      return Math.floor((toDay - fromDay) / DAY_MS);
    };

    const planRaw = String(currentUser?.company_plan || currentUser?.role || '').trim().toUpperCase();
    const planMap = {
      TRIAL: 'Trial',
      STANDARD: 'Standard',
      ENTERPRISE: 'Business'
    };

    const now = new Date();
    const planAlias = { UNITARIO: 'STANDARD', STAND: 'STANDARD', BUSINESS: 'ENTERPRISE' };
    const planResolved = planAlias[planRaw] || planRaw;
    const normalizedPlan = ['TRIAL', 'STANDARD', 'ENTERPRISE'].includes(planResolved) ? planResolved : null;
    const planLabel = planMap[normalizedPlan] || '\u2014';
    const isTrial = normalizedPlan === 'TRIAL';
    const companyName = String(currentUser?.company_name || '').trim().toLowerCase();
    const isTacontudo = companyName.includes('tacontudo');
    const trialTotalDays = 15;

    let since = null;
    let expiresAt = null;
    let daysUntilExpiry = null;
    let daysActive = null;
    let trialDaysUsed = 0;
    let alertLevel = null;
    let statusLabel = '\u2014';
    let statusType = 'neutral';

    if (isTrial) {
      since = parseDate(currentUser?.created_at);
      if (since) {
        expiresAt = new Date(since.getTime() + (trialTotalDays * DAY_MS));
        trialDaysUsed = Math.max(0, diffDays(since, now));
        daysActive = trialDaysUsed;
        daysUntilExpiry = diffDays(now, expiresAt);
      }

      if (daysUntilExpiry !== null && daysUntilExpiry <= 3) {
        alertLevel = 'danger';
      } else if (daysUntilExpiry !== null && daysUntilExpiry <= 7) {
        alertLevel = 'warning';
      }

      if (daysUntilExpiry === null) {
        statusLabel = '\u2014';
        statusType = 'neutral';
      } else if (daysUntilExpiry >= 0) {
        statusLabel = 'Ativo';
        statusType = 'active';
      } else {
        statusLabel = 'Expirado';
        statusType = 'danger';
      }
    } else if (normalizedPlan === 'STANDARD' || normalizedPlan === 'ENTERPRISE') {
      since = parseDate(currentUser?.company_plan_updated_at) || parseDate(currentUser?.created_at);
      expiresAt = parseDate(currentUser?.company_plan_expires_at);
      if (since) {
        daysActive = Math.max(0, diffDays(since, now));
      }
      if (expiresAt) {
        daysUntilExpiry = diffDays(now, expiresAt);
        if (daysUntilExpiry <= 3) {
          alertLevel = 'danger';
        } else if (daysUntilExpiry <= 7) {
          alertLevel = 'warning';
        }
      }

      const billingStatus = String(currentUser?.company_billing_status || '').trim().toLowerCase();
      if (billingStatus === 'active') {
        statusLabel = 'Ativo';
        statusType = 'active';
      } else if (billingStatus === 'past_due') {
        statusLabel = 'Pagamento pendente';
        statusType = 'warning';
      } else if (billingStatus === 'canceled') {
        statusLabel = 'Cancelado';
        statusType = 'danger';
      } else {
        const roleUp = String(currentUser?.role || '').toUpperCase();
        if (roleUp === 'UNITARIO' || roleUp === 'QA' || roleUp === 'DEV' || roleUp === 'GERENCIAL') {
          // Usuário aprovado manualmente — sem billing_status mas está ativo
          statusLabel = 'Ativo';
          statusType = 'active';
        } else {
          statusLabel = '\u2014';
          statusType = 'neutral';
        }
      }
    }

    const sinceLabel = since
      ? since.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : null;

    return {
      planLabel,
      statusLabel,
      statusType,
      since,
      sinceLabel,
      daysActive,
      expiresAt,
      daysUntilExpiry,
      isTrial,
      isTacontudo,
      trialTotalDays,
      trialDaysUsed,
      alertLevel
    };
  };

  const planInfo = getPlanInfo(user);

  const openSupportModal = () => {
    setSupportSubject('Dúvida no sistema');
    setSupportMessage('');
    setIsSupportModalOpen(true);
  };

  const closeSupportModal = () => {
    if (isSendingSupport) return;
    setIsSupportModalOpen(false);
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    const trimmedMessage = String(supportMessage || '').trim();
    const trimmedSubject = String(supportSubject || '').trim() || 'Contato via sistema';

    if (trimmedMessage.length < 10) {
      showError('Escreva uma mensagem com pelo menos 10 caracteres.');
      return;
    }
    if (trimmedMessage.length > 1000) {
      showError('Sua mensagem deve ter no máximo 1000 caracteres.');
      return;
    }

    setIsSendingSupport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        getApiUrl('support/contact'),
        { subject: trimmedSubject, message: trimmedMessage },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } }
      );

      if (response.data?.success) {
        showSuccess('Mensagem enviada para o suporte com sucesso.');
        setIsSupportModalOpen(false);
        setSupportMessage('');
      } else {
        showError(response.data?.error || 'Não foi possível enviar para o suporte.');
      }
    } catch (error) {
      const apiError = error?.response?.data?.error;
      showError(apiError || 'Erro ao enviar para o suporte. Tente novamente.');
    } finally {
      setIsSendingSupport(false);
    }
  };

  return (
    <div className="overview">
      <main className="overview-main">
        <div className="overview-container">
          <header className="overview-header">
            <h1>🌟 Visão Geral do Sistema</h1>
            <p>Acesse rapidamente todas as funcionalidades do QA'doro</p>
            <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {tutorial?.isTutorialMode && (
                <button
                  type="button"
                  className="support-btn secondary"
                  onClick={() => {
                    navigate('/overview');
                    setTimeout(() => tutorial?.finish?.(), 0);
                  }}
                >
                  Finalizar tutorial
                </button>
              )}
            </div>
          </header>

          <div className="overview-layout">
            <div className="cards-area">
              {Object.entries(cardSections).map(([sectionKey, section]) => {
                const visibleCards = section.cards.filter((card) => {
                  return (
                    card.id === 'projects' ? hasPermission('canManageProjects', ['ADM', 'ADMIN', 'GERENCIAL']) :
                    card.id === 'dashboard' ? hasPermission('canViewReports', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'test-repository' ? hasPermission('testcases.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'executions' ? hasPermission('executions.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'reports' ? hasPermission('canViewReports', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'status' ? hasPermission('status.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'issues' ? hasPermission('issues.read', ['ADM', 'ADMIN', 'GERENCIAL', 'DEV', 'QA']) :
                    card.id === 'user-management' ? (
                      !isTrialOrUnitario && (
                      hasPermission('roles.read', ['ADM', 'ADMIN', 'GERENCIAL']) ||
                      hasPermission('groups.read', ['ADM', 'ADMIN', 'GERENCIAL']) ||
                      hasPermission('canCreateUser', ['ADM', 'ADMIN', 'GERENCIAL']) ||
                      hasPermission('canEditUser', ['ADM', 'ADMIN', 'GERENCIAL'])
                      )
                    ) :
                    true
                  );
                });

                if (visibleCards.length === 0) return null;

                return (
                  <section key={sectionKey} className="card-section">
                    <div className="section-header">
                      <h2>
                        <span className="section-icon">{section.icon}</span>
                        {section.title}
                      </h2>
                    </div>

                    <div className="cards-grid">
                      {visibleCards.map((card) => (
                        <div
                          key={card.id}
                          className="overview-card"
                          onClick={() => handleCardClick(card.route)}
                          data-tour={card.id === 'test-repository' ? 'nav-test-repository' : undefined}
                        >
                          <div className="card-icon">{card.icon}</div>
                          <div className="card-content">
                            <h3>{card.title}</h3>
                            <p>{card.description}</p>
                          </div>
                          <div className="card-arrow">→</div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            <aside className="sidebar-content">
              <div className="plan-block">
                <div className="plan-header">
                  <h3>📋 Meu Plano</h3>
                </div>
                <div className="plan-content">
                  <div className="plan-name-row">
                    <span className="plan-name">{planInfo.planLabel}</span>
                    <span className={`plan-status-badge plan-status-${planInfo.statusType}`}>
                      {planInfo.statusLabel}
                    </span>
                  </div>

                  {planInfo.isTrial && !planInfo.isTacontudo && (
                    <div className="plan-progress-area">
                      <div className="plan-progress-bar-bg">
                        <div
                          className={`plan-progress-bar-fill ${planInfo.alertLevel ? `plan-alert-${planInfo.alertLevel}` : ''}`}
                          style={{ width: `${Math.min(100, (planInfo.trialDaysUsed / planInfo.trialTotalDays) * 100)}%` }}
                        />
                      </div>
                      <span className="plan-progress-label">
                        {planInfo.trialDaysUsed} de {planInfo.trialTotalDays} dias usados
                      </span>
                    </div>
                  )}

                  {planInfo.sinceLabel && (
                    <div className="plan-since">
                      Ativo desde: <strong>{planInfo.sinceLabel}</strong>
                    </div>
                  )}

                  {!planInfo.isTacontudo && planInfo.daysUntilExpiry !== null && planInfo.daysUntilExpiry >= 0 && (
                    <div className={`plan-expiry plan-alert-${planInfo.alertLevel || 'none'}`}>
                      {planInfo.isTrial
                        ? `Restam ${planInfo.daysUntilExpiry} dias de trial`
                        : `Renova em ${planInfo.daysUntilExpiry} dias`
                      }
                    </div>
                  )}

                  {!planInfo.isTacontudo && planInfo.daysUntilExpiry !== null && planInfo.daysUntilExpiry < 0 && (
                    <div className="plan-expiry plan-alert-danger">
                      {planInfo.isTrial ? 'Trial expirado' : 'Plano expirado'}
                    </div>
                  )}
                </div>
              </div>

              {/* Bloco de Suporte */}
              <div className="support-block">
                <div className="support-header">
                  <h3>💬 Precisa de Ajuda?</h3>
                </div>
                <div className="support-content">
                  <p>
                    Nossa equipe está sempre pronta para ajudar você a aproveitar 
                    ao máximo o QA'doro. Entre em contato conosco!
                  </p>
                  <div className="support-actions">
                    <button 
                      className="support-btn primary"
                    onClick={openSupportModal}
                    >
                      📧 Suporte e Contato
                    </button>
                  </div>
                </div>
              </div>

              {/* Status de Integrações */}
              <div className="integrations-block">
                <div className="integrations-header">
                  <h3>🔗 Integrações</h3>
                </div>
                <div className="integrations-content">
                  {integrations.length === 0 ? (
                    <p className="integrations-empty">Nenhuma integração carregada</p>
                  ) : (
                    integrations.map((item) => (
                      <div key={item.key} className="integration-item">
                        <div className="integration-meta">
                          <span className="integration-icon">{item.icon}</span>
                          <span className="integration-title">{item.title}</span>
                        </div>
                        <div className="integration-status">
                          <span className={`status-pill ${item.configured ? 'configured' : 'not-configured'}`}>
                            {item.configured ? 'Configurado' : 'Não configurado'}
                          </span>
                          <span className={`status-pill ${item.active ? 'active' : 'inactive'}`}>
                            {item.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="integrations-footer">
                  <button className="go-integrations" onClick={() => navigate('/integrations-v2')}>
                    Abrir página de Integrações V2 →
                  </button>
                </div>
              </div>

              {/* Área de Notícias/Updates */}
              <div className="news-block">
                <div className="news-header">
                  <h3>📰 Novidades do Sistema</h3>
                </div>
                <div className="news-content">
                  {newsUpdates.map((news) => (
                    <div key={news.id} className={`news-item ${getNewsTypeClass(news.type)}`}>
                      <div className="news-icon">{news.icon}</div>
                      <div className="news-details">
                        <h4>{news.title}</h4>
                        <p>{news.description}</p>
                        <span className="news-date">{formatDate(news.date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="news-footer">
                  <button 
                    className="view-all-news"
                    onClick={() => navigate('/changelog')}
                  >
                    Ver todas as atualizações →
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      {isSupportModalOpen && (
        <div className="support-modal-overlay" onClick={closeSupportModal}>
          <div className="support-modal" onClick={(e) => e.stopPropagation()}>
            <div className="support-modal-header">
              <h3>Contato com Suporte</h3>
              <button
                type="button"
                className="support-modal-close"
                onClick={closeSupportModal}
                disabled={isSendingSupport}
                aria-label="Fechar modal"
              >
                ×
              </button>
            </div>
            <form className="support-modal-body" onSubmit={handleSupportSubmit}>
              <div className="support-modal-field">
                <label htmlFor="support-email">Seu e-mail</label>
                <input id="support-email" type="email" value={user?.email || ''} disabled />
              </div>
              <div className="support-modal-field">
                <label htmlFor="support-subject">Assunto</label>
                <input
                  id="support-subject"
                  type="text"
                  value={supportSubject}
                  onChange={(e) => setSupportSubject(e.target.value)}
                  maxLength={120}
                />
              </div>
              <div className="support-modal-field">
                <label htmlFor="support-message">Sua dúvida</label>
                <textarea
                  id="support-message"
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  placeholder="Descreva sua dúvida para nossa equipe..."
                  minLength={10}
                  maxLength={1000}
                  rows={7}
                  required
                />
                <small>{supportMessage.length}/1000</small>
              </div>
              <div className="support-modal-actions">
                <button
                  type="button"
                  className="support-btn secondary"
                  onClick={closeSupportModal}
                  disabled={isSendingSupport}
                >
                  Cancelar
                </button>
                <button type="submit" className="support-btn primary" disabled={isSendingSupport}>
                  {isSendingSupport ? 'Enviando...' : 'Enviar para Suporte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
